import fetch from 'isomorphic-fetch'
import {
    ApolloClient,
    InMemoryCache,
    NormalizedCacheObject,
    createHttpLink,
} from '@apollo/client'

import { authLink } from '~utils/auth-helpers'

let browserApolloClient: ApolloClient<NormalizedCacheObject> | undefined

export const createApolloClient = (): ApolloClient<NormalizedCacheObject> => {
    const link = createHttpLink({
        uri: process.env.GATSBY_API_URL || 'https://dev.api.chckup.app/graphql',
        fetch,
    })

    return new ApolloClient({
        link: authLink.concat(link),
        cache: new InMemoryCache({
            typePolicies: {
                FormElement: {
                    // Don't normalize FormElement objects - they should always be part of their parent
                    keyFields: false,
                },
                FormFields: {
                    // Don't normalize FormFields objects - they should always be part of their parent
                    keyFields: false,
                },
                Clinic: {
                    fields: {
                        last_sync_times: {
                            merge(existing, incoming) {
                                // Merge the last_sync_times object by combining all fields
                                return {
                                    ...existing,
                                    ...incoming,
                                }
                            },
                        },
                        address: {
                            merge(existing, incoming) {
                                return {
                                    ...existing,
                                    ...incoming,
                                }
                            },
                        },
                        settings: {
                            merge(
                                existing,
                                incoming,
                                { isReference, mergeObjects, readField },
                            ) {
                                if (incoming == null) return incoming
                                if (existing == null) return incoming

                                const existingId = isReference(existing)
                                    ? readField<string>('id', existing)
                                    : (existing as { id?: string }).id
                                const incomingId = isReference(incoming)
                                    ? readField<string>('id', incoming)
                                    : (incoming as { id?: string }).id

                                // Inbox message queries used to write Clinic.settings
                                // without `id`, which replaced the normalized
                                // ClinicSettings entity and dropped
                                // primary_clinic_group_id. Keep the identified
                                // record when the incoming value is a stub.
                                if (existingId && !incomingId) {
                                    return existing
                                }

                                return mergeObjects(existing, incoming)
                            },
                        },
                    },
                },
                AvailabilityHours: {
                    fields: {
                        monday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        tuesday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        wednesday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        thursday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        friday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        saturday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        sunday: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                    },
                },
                Availability: {
                    fields: {
                        times: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                        repeat: {
                            merge(existing, incoming) {
                                return { ...existing, ...incoming }
                            },
                        },
                    },
                },
                ClinicSettings: {
                    merge: true,
                },
                Address: {
                    merge: true,
                },
            },
        }),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'cache-first',
            },
        },
    })
}

export const getApolloClient = (): ApolloClient<NormalizedCacheObject> => {
    if (typeof window === 'undefined') {
        return createApolloClient()
    }

    if (!browserApolloClient) {
        browserApolloClient = createApolloClient()
    }

    return browserApolloClient
}
